/**
 * Per-run logs drill-in — the general (non-api_request) log records for one run,
 * from `useLogs` (GET /v1/runs/{platform}/{session_id}/logs).
 *
 * READ-ONLY. A severity `<select>` sets the FLOOR filter (default INFO); changing
 * it re-keys `useLogs`, which refetches from the first page. Cursor paging is
 * surfaced as a `Load more` button (visible only while `hasNextPage`); pages are
 * flattened and rendered in order so each `Load more` appends to the list.
 *
 * Bodies/attrs are opaque — the service never interprets them, and neither does
 * this view. `body_truncated` / `attrs_truncated` mark values the receiver
 * clipped; the marker just flags that, it does not reconstruct anything.
 */

import { useState } from 'react';
import { useLogs } from '../lib/queries';

/** The severity ladder, ascending. The floor filter matches this level and
 *  everything above it. */
export const SEVERITIES = ['TRACE', 'DEBUG', 'INFO', 'WARN', 'ERROR', 'FATAL'] as const;

/** The CSS class dressing a severity span, e.g. `WARN` → `log-sev-warn`. Colors
 *  are mapped to Saga tokens in index.css (muted trace/debug, text info,
 *  banner-text warn, danger error/fatal). */
export function severityClass(s: string): string {
  return `log-sev-${s.toLowerCase()}`;
}

export default function LogsPanel({
  platform,
  sessionId,
}: {
  platform: string;
  sessionId: string;
}) {
  const [severity, setSeverity] = useState('INFO');
  const { data, hasNextPage, fetchNextPage, isLoading, isError, refetch } = useLogs(
    platform,
    sessionId,
    severity,
  );

  const lines = (data?.pages ?? []).flatMap((p) => p.logs);

  const control = (
    <div className="logs-controls">
      <label className="runs-control">
        <span className="runs-control-label">Severity ≥</span>
        <select value={severity} onChange={(e) => setSeverity(e.target.value)}>
          {SEVERITIES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>
    </div>
  );

  return (
    <>
      <h3 className="section-title">Logs</h3>
      {control}

      {isLoading ? (
        <p className="note">Loading logs…</p>
      ) : isError ? (
        <p className="error" role="alert">
          Failed to load logs{' '}
          <button type="button" className="btn" onClick={() => refetch()}>
            Retry
          </button>
        </p>
      ) : lines.length === 0 ? (
        <p className="note">No logs for this run.</p>
      ) : (
        <>
          <ul className="log-lines">
            {lines.map((line) => (
              <li key={`${line.platform}#${line.session_id}#${line.seq}`} className="log-line">
                <span className="log-ts mono">{line.ts_iso}</span>
                <span className={`log-sev ${severityClass(line.severity)}`}>{line.severity}</span>
                {line.event_name && <span className="log-event">{line.event_name}</span>}
                <code className="log-body">{String(line.body)}</code>
                {line.body_truncated && <span className="truncated-marker">…truncated</span>}
                {line.attrs && Object.keys(line.attrs).length > 0 && (
                  <details className="log-attrs">
                    <summary>attrs{line.attrs_truncated ? ' (truncated)' : ''}</summary>
                    <pre>
                      <code>{JSON.stringify(line.attrs, null, 2)}</code>
                    </pre>
                  </details>
                )}
              </li>
            ))}
          </ul>
          {hasNextPage && (
            <button type="button" className="btn" onClick={() => fetchNextPage()}>
              Load more
            </button>
          )}
        </>
      )}
    </>
  );
}
